import {firebaseApp} from "../config/firebase"
import {getFirestore, collection, query, getDocs, where, onSnapshot, orderBy, doc, serverTimestamp, setDoc} from "firebase/firestore";

import {logError} from "../utils/errorHandlingUtils"
import {DAY_IN_MILLISECONDS} from "../constants/datetime"

export const SAVE_PAYEE_ORDERS = 'SAVE_PAYEE_ORDERS'
export const UPDATE_PAYEE_ORDER = 'UPDATE_PAYEE_ORDER'
export const savePayeeOrders = (payeeOrders, lastLoadedAt=0) => {
    return {
        type: SAVE_PAYEE_ORDERS,
        payload: {
            payeeOrders,
            lastLoadedAt
        }
    }
}

export const updatePayeeOrder = (payeeOrderId, update={}) => {
    return {
        type: UPDATE_PAYEE_ORDER,
        payload: {
            payeeOrderId,
            update
        }
    }
}


export const fetchSaveMarketplacePayeeOrdersInDateRange = (
    fromDate=0,
    toDate=Date.now(),
    marketplaceId=""
) => {
    /**
     * Purpose: retrieve the payee orders for a specific marketplace in the date range specified from the firestore database
     */
    const firestore = getFirestore(firebaseApp)
    const payeeOrdersRef = query(collection(firestore, "payeeOrders"),
        where("createdAt", ">=", fromDate),
        where("createdAt", "<=", toDate),
        where("marketplaceId", "==", marketplaceId),
    )

    return async dispatch => {
        try {
            const querySnapshot = await getDocs(payeeOrdersRef)
            //get an array of payeeOrders from the snapshot
            const payeeOrders = querySnapshot.docs.map(docRef => ({...docRef.data()}));
            dispatch(savePayeeOrders(payeeOrders, Date.now()))
            return payeeOrders
        } catch (e) {
            const message = `action > payeeOrders > fetchSaveMarketplacePayeeOrdersInDateRange: Failed to save payee orders for from ${fromDate} to ${toDate} for marketplace ${marketplaceId}`
            logError(e, {message})
            return []
        }
    }
}

export const fetchSubscribeToOpenMarketplacePayeeOrders = (marketplaceId="") => {
    /**
     * Purpose: retrieve the open payee orders for the marketplace from the firestore db
     * Note: the onSnapshot below watches for changes on the server
     */
    const firestore = getFirestore(firebaseApp)
    const payeeOrdersRef = query(collection(firestore,  "payeeOrders"),
                                 where("paidAt", ">", 0),
                                 where("closedAt", "==", null),
                                 where("marketplaceId", "==", marketplaceId),
                                 orderBy("paidAt", "desc")
    )
    return async dispatch => {
        try {
            const payeeOrdersListener = onSnapshot(payeeOrdersRef, 
                querySnapshot => {
                    //get an array of payeeOrders from the snapshot
                    const payeeOrders = querySnapshot.docs.map(docRef => ({...docRef.data()}))
                    dispatch(savePayeeOrders(payeeOrders, Date.now()))
                }
            )
            return payeeOrdersListener
        } catch (e) {
            const message = `action > payeeOrders > fetchSubscribeToOpenMarketplacePayeeOrders: Failed to subscribe to open payee orders for marketplace ${marketplaceId}`
            logError(e, {message})
            return () => {}
        }
    }
}

export const fetchSubscribeToRecentlyClosedMarketplacePayeeOrders = (marketplaceId, fromDate=(Date.now() - DAY_IN_MILLISECONDS)) => {
    /**
     * Purpose: retrieve the marketplace's recently closed payee orders from the firestore db
     * Note: the onSnapshot below watches for changes on the server
     */
    const firestore = getFirestore(firebaseApp)
    const payeeOrdersRef = query(collection(firestore,  "payeeOrders"),
                                 where("closedAt", ">", fromDate),
                                 where("marketplaceId", "==", marketplaceId),
                                 orderBy("closedAt", "desc")
    )
    return async dispatch => {
        try {
            const payeeOrdersListener = onSnapshot(payeeOrdersRef, 
                querySnapshot => {
                    //get an array of payeeOrders from the snapshot
                    const payeeOrders = querySnapshot.docs.map(docRef => ({...docRef.data()}));
                    dispatch(savePayeeOrders(payeeOrders, Date.now()))
                }
            )
            return payeeOrdersListener
        } catch (e) {
            const message = `action > payeeOrders > fetchSubscribeToRecentlyClosedMarketplacePayeeOrders: Failed to subscribe to recently closed payee orders for marketplace ${marketplaceId}`
            logError(e, {message})
            return () => {}
        }
    }
}

export const fetchUpdatePayeeOrder = (
    id,
    update={},
    onSuccess=()=>{},
    onError=()=>{}
 ) => {
    const firestore = getFirestore(firebaseApp)
    const payeeOrdersRef = doc(firestore, "payeeOrders", id)
    return async (dispatch, getState) => {
        try{
            const {user, payeeOrders} = getState()
            const payeeOrder = payeeOrders.payeeOrdersById[id]
            if (!payeeOrder) throw new Error(`Payee Order ${id} not found in redux`)
            update.lastUpdatedByUserId = user.id
            update.lastUpdatedAt = Date.now()
            update.lastUpdatedAtTimestamp = serverTimestamp()
            await setDoc(payeeOrdersRef, update, {merge:true})
            dispatch(updatePayeeOrder(id, update))
            onSuccess(update)
            return true
        } catch (e){
            const message = `action > payeeOrders > fetchUpdatePayeeOrder: Failed to update Payee Order ${id} with update ${JSON.stringify(update)}`
            logError(e, {message})
            onError(update)
            return false
        }
    }
}